import { request } from '../api/client';
import { CONDITION } from './catalog';
import type { CatalogRow } from './catalog';

/**
 * Шины и диски.
 *
 * <p>Это товар с типом, а не отдельная сущность: склад, резерв и продажа
 * у них общие с запчастями. Отличается набор свойств — и то, что заводят
 * их комплектом, а продают поштучно: на разборке снимают четыре колеса
 * разом, но покупатель берёт и одну запаску.
 */

/**
 * Сезоны. Зимняя разделена на шипы и липучку: разница не косметическая —
 * ездят на них иначе, стоят по-разному, и покупатель спрашивает именно это.
 * Прежняя «зимняя» осталась ради уже заведённых шин: у них неизвестно,
 * какие они, а додумывать за приёмщика нельзя.
 */
export const SEASONS = [
  { code: 'SUMMER', name: 'летняя' },
  { code: 'WINTER_STUDDED', name: 'зимняя (шипы)' },
  { code: 'WINTER_FRICTION', name: 'зимняя (липучка)' },
  { code: 'ALL_SEASON', name: 'всесезонная' },
  { code: 'WINTER', name: 'зимняя' },
] as const;

/** Виды товара вкладки: у колеса в сборе заполнены оба набора свойств. */
export const WHEEL_KINDS = [
  { code: 'TYRE', name: 'Шина' },
  { code: 'DISC', name: 'Диск' },
  { code: 'ASSEMBLY', name: 'Колесо' },
] as const;

export function kindName(kind: string): string {
  return WHEEL_KINDS.find((k) => k.code === kind)?.name ?? kind;
}

export interface Wheel {
  id: number;
  /**
   * Порядковый номер позиции — тот, которым колесо называют в разговоре:
   * «посмотри позицию 347». Нумерация общая с запчастями (`part.number`),
   * и это главное: две нумерации рядом дали бы две «позиции 347».
   */
  number: number;
  publicCode: string | null;
  title: string;
  price: number | null;
  status: string;
  qty: number;
  kind: string;
  /** Пусто — колесо заведено поштучно, а не комплектом. */
  setNo: number | null;
  /**
   * Колёс в позиции, продаваемой одним комплектом, — за столько названа цена
   * и столько уходит в колёсный прайс (перенос с Дрома, задача 0291). Пусто —
   * колесо продаётся поштучно. Ответ прежней версии поля не несёт.
   */
  setPieces?: number | null;
  diameter: number | null;
  tyreWidth: number | null;
  tyreHeight: number | null;
  construction: string | null;
  tyreType: string | null;
  season: string | null;
  /** Износ шины в процентах, как у клиента в Bazon («30%»), задача 0340. */
  wearPercent: number | null;
  madeYear: number | null;
  discType: string | null;
  discWidth: number | null;
  offsetMm: number | null;
  boltPattern: string | null;
  hubBore: number | null;
  brand: string | null;
  model: string | null;
  discBrand: string | null;
  discModel: string | null;
  markingType: string | null;
  treadType: string | null;
  runFlat: boolean | null;
  lightTruck: boolean | null;
  speedIndex: string | null;
  loadIndex: number | null;
  partName: string | null;
  condition: string | null;
  supply: string | null;
  donorCode: string | null;
  oem: string | null;
  description: string | null;
  note: string | null;
  section: string | null;
  published: boolean;
  barcode: string | null;
  legacyCode: string | null;
  photoCount: number;
  createdAt: string | null;
  updatedAt: string | null;
  updatedByName: string | null;
  priceChangedAt: string | null;
  priceChangedByName: string | null;
  /** Остаток по складам: ключ — идентификатор склада. */
  stock: Record<string, number>;
}

/** Строка витрины: свойства колеса плюс подписанная ссылка на превью. */
export interface WheelRow {
  wheel: Wheel;
  photoUrl: string | null;
}

export interface WheelWarehouse {
  id: number;
  name: string;
}

export interface WheelPage {
  /** Сколько всего под отбором, а не сколько строк пришло. */
  total: number;
  warehouses: WheelWarehouse[];
  rows: WheelRow[];
  /** По каким колонкам сервер делает отбор — см. CatalogPage. */
  filterable: string[];
}

/**
 * Отбор вкладки колёс.
 *
 * <p>Поиск идёт по номеру товара и заголовку разом: размер попадает в него
 * сам, потому что собран в заголовок («Шина 195/65 R15 …»), а покупатель
 * называет именно размер.
 */
export interface WheelQuery {
  q: string;
  /** Пусто — оба вида: у шины и диска половина колонок разная. */
  kind: '' | 'TYRE' | 'DISC' | 'ASSEMBLY';
  missing: boolean;
  /**
   * Отбор колонками, как в кабинете: ключ колонки → выбранное значение.
   * Значение выбирается из списка того, что есть на складе, а не набирается
   * руками — набрав «16» там, где на складе только пятнадцатые, человек
   * получает пустую таблицу и не понимает, ошибся он или товара нет.
   */
  columns: Record<string, string>;
  /** Страница, считая с нуля: у крупной шинки колёс больше, чем влезает в одну. */
  page: number;
  /**
   * Вбитое в колонку руками: ищется вхождением, а не точным равенством.
   * Список нужен, когда значений десяток; когда их сотня — быстрее набрать
   * три буквы, чем искать глазами.
   */
  words: Record<string, string>;
  sort: string;
  desc: boolean;
}

/** Незаполненное поле и «заполнено хоть чем-то» — тоже ответы на вопрос. */
export const FILTER_EMPTY = '\u2014пусто\u2014';
export const FILTER_PRESENT = '\u2014не пусто\u2014';

export const WHEEL_PAGE_SIZE = 50;

/**
 * Порядок по умолчанию — номер позиции по возрастанию, как на витрине
 * склада: «что принято раньше, то выше». До этого вкладка открывалась
 * номером комплекта по убыванию, то есть два соседних экрана открывались
 * по-разному без причины, а колёса, заведённые поштучно, вовсе не имеют
 * номера комплекта и уезжали в конец.
 */
export const EMPTY_WHEEL_QUERY: WheelQuery = {
  q: '', kind: '', missing: false, columns: {}, words: {}, sort: 'number', desc: false, page: 0,
};

/** Задан ли хоть один отбор: по этому экран решает, показывать ли «Сбросить». */
export function hasWheelFilters(query: WheelQuery): boolean {
  return query.q.trim() !== '' || query.kind !== '' || query.missing
    || Object.keys(query.columns ?? {}).length > 0
    || Object.keys(query.words ?? {}).length > 0;
}

/** Значения колонки — по нажатию на заголовок, а не вместе со страницей. */
export function wheelValues(column: string): Promise<string[]> {
  return request<string[]>(`/api/wheels/values?column=${encodeURIComponent(column)}`);
}

/** Параметры отбора одни и у страницы, и у выгрузки: файл обязан совпасть. */
export function wheelParams(query: WheelQuery): URLSearchParams {
  const params = new URLSearchParams();
  // Пустой отбор — «без ограничения», а не «ничего».
  if (query.q.trim() !== '') params.set('q', query.q.trim());
  if (query.kind !== '') params.set('kind', query.kind);
  if (query.missing) params.set('missing', 'true');
  // Пара на каждый фильтр: «колонка:значение». Одним параметром их не свести —
  // значение может содержать что угодно, включая запятую.
  // Пустое по умолчанию: состояние, оставшееся от прежней версии кода,
  // не должно ронять экран целиком. Выкат случается, пока вкладка открыта,
  // и белый экран вместо таблицы — худшее, чем это может кончиться.
  for (const [column, value] of Object.entries(query.columns ?? {})) {
    params.append('filter', `${column}:${value}`);
  }
  for (const [column, value] of Object.entries(query.words ?? {})) {
    if (value.trim() !== '') params.append('find', `${column}:${value.trim()}`);
  }
  params.set('sort', query.sort);
  params.set('desc', String(query.desc));
  return params;
}

export function listWheels(query: WheelQuery = EMPTY_WHEEL_QUERY): Promise<WheelPage> {
  const params = wheelParams(query);
  params.set('page', String(query.page ?? 0));
  params.set('size', String(WHEEL_PAGE_SIZE));
  return request<WheelPage>(`/api/wheels?${params.toString()}`);
}

/**
 * Ссылка на выгрузку, а не запрос из скрипта: файл качает браузер, показывая
 * ход, и вкладка при этом жива.
 */
export function wheelExportUrl(query: WheelQuery): string {
  return `/api/wheels/export?${wheelParams(query).toString()}`;
}

export interface SetRequest {
  kind: 'TYRE' | 'DISC' | 'ASSEMBLY';
  warehouseId: number;
  quantity: number;
  diameter: string | null;
  tyreWidth: string | null;
  tyreHeight: string | null;
  season: string | null;
  wearPercent: string | null;
  madeYear: string | null;
  discType: string | null;
  discWidth: string | null;
  offsetMm: string | null;
  boltPattern: string | null;
  hubBore: string | null;
  brand: string | null;
  model: string | null;
  discBrand: string | null;
  discModel: string | null;
  tyreType: string | null;
  construction: string | null;
  markingType: string | null;
  treadType: string | null;
  runFlat: boolean | null;
  lightTruck: boolean | null;
  speedIndex: string | null;
  loadIndex: string | null;
  price: string | null;
}

export function createSet(
  request_: SetRequest,
): Promise<{ setNo: number | null; title: string; partIds: number[] }> {
  return request('/api/wheels/sets', { method: 'POST', body: request_ });
}

/**
 * Размер строкой: «195/65 R15» для шины, «6.0x15 5x100 ET45» для диска.
 *
 * <p>В списке это первое, на что смотрят: покупатель называет размер,
 * а не название модели.
 */
export function sizeOf(wheel: Wheel): string {
  // У колеса в сборе размер — шинный: «225/55 R18». Дисковый у него тоже
  // есть, но ищут его по резине, а сверловка и вылет стоят своими полями.
  if (wheel.kind !== 'DISC') {
    const profile =
      wheel.tyreWidth !== null && wheel.tyreHeight !== null
        ? `${wheel.tyreWidth}/${wheel.tyreHeight}`
        : '';
    const rim = wheel.diameter === null ? '' : `${wheel.construction ?? 'R'}${wheel.diameter}`;
    return [profile, rim].filter((part) => part !== '').join(' ');
  }

  const size =
    wheel.discWidth !== null && wheel.diameter !== null
      ? `${wheel.discWidth}x${wheel.diameter}`
      : '';
  return [size, wheel.boltPattern, wheel.offsetMm === null ? '' : `ET${wheel.offsetMm}`]
    .filter((part) => part !== null && part !== '')
    .join(' ');
}

/**
 * Колонки вкладки «Шины и диски».
 *
 * <p>Список и порядок сняты с кабинета клиента (221 товар, 45 колонок).
 * Порядок не случайный: сначала то, по чему шину подбирают — размер, сезон,
 * износ, — и только потом служебное. Половина колонок относится либо
 * к шине, либо к диску, и у второго товара они честно пусты: одна таблица
 * на оба вида — это решение кабинета, и покупатель ищет колесо целиком,
 * а не отдельно резину и отдельно железо.
 */
export interface WheelColumn {
  key: string;
  title: string;
  /** Имя сортировки на сервере. Нет — по этой колонке не сортируют. */
  sort?: string;
  /** false — по колонке не отбирают: у превью значений нет. */
  filter?: false;
  numeric?: boolean;
  fixed?: boolean;
  value: (w: Wheel) => string;
  image?: (row: WheelRow) => string | null;
}

const MARKING: Record<string, string> = {
  METRIC: 'Метрическая',
  INCH: 'Дюймовая',
  FLOTATION: 'Флотационная',
};

const TREAD: Record<string, string> = {
  STANDARD: 'Стандартный',
  ASYMMETRIC: 'Асимметричный',
  DIRECTIONAL: 'Направленный',
};

// Состояние берётся общим словарём (`catalog.ts`), а не пишется здесь:
// копия расходилась бы с витриной склада на первом же новом значении,
// и расходилась бы молча — выбранное из списка перестало бы находиться
// (задача 0039).

/** Износ процентом со знаком, как пишет Bazon: «30%»; пусто — не указан. */
/**
 * Слова отказа сервера по износу (задача 0345): по ним экран понимает, что
 * отказ про это поле, и ставит его у поля. Обязаны совпадать с
 * {@code WheelService.WEAR_WORDS}; разойдясь, отказ уйдёт строкой над формой —
 * слова всё равно дойдут.
 */
export const WEAR_WORDS = 'Износ шины — целое число процентов от 0 до 100';

function wearText(percent: number | null | undefined): string {
  return percent == null ? '' : `${percent}%`;
}

function text(value: string | number | null): string {
  return value === null || value === undefined ? '' : String(value);
}

function money(value: number | null): string {
  return value === null ? '' : value.toLocaleString('ru-RU');
}

/** Дата без времени: время правки в таблице — шум. */
function day(value: string | null): string {
  return value === null ? '' : new Date(value).toLocaleDateString('ru-RU');
}

/** Пусто, а не «нет»: флажок сообщает что-то только когда он поднят. */
function flag(value: boolean | null): string {
  return value === true ? 'да' : '';
}

export const WHEEL_COLUMNS: WheelColumn[] = [
  // Первой колонкой и по ней же порядок по умолчанию — ровно как на витрине
  // склада: колесо это та же `part`, и номер у него из той же нумерации.
  // Два соседних экрана, открывающиеся по-разному, различаются без причины,
  // а «позиция 347», названная по телефону, обязана значить одно и то же
  // на обоих. Отбора по нему нет — тридцать пять тысяч значений в списке
  // не выбирают, — поэтому `filter: false`, как у «Создан».
  { key: 'number', title: '№ позиции', sort: 'number', numeric: true, filter: false,
    value: (w) => text(w.number) },
  { key: 'code', title: 'Номер товара', sort: 'code', value: (w) => text(w.publicCode) },
  // Вторым столбцом, как в кабинете: колесо узнают по картинке — диски
  // различаются только рисунком, и словами его не опишешь.
  { key: 'photo', title: 'Превью', fixed: true, filter: false, value: () => '',
    image: (row) => row.photoUrl },
  { key: 'set', title: 'Номер комплекта', sort: 'set', numeric: true, value: (w) => text(w.setNo) },
  // Не путать с номером комплекта: тот связывает отдельные карточки, а здесь
  // одна позиция и есть комплект, продаваемый только целиком (задача 0291).
  { key: 'setPieces', title: 'Штук в комплекте', sort: 'setPieces', numeric: true,
    value: (w) => text(w.setPieces ?? null) },
  { key: 'kind', title: 'Товар', sort: 'kind', value: (w) => kindName(w.kind) },
  { key: 'diameter', title: 'Диаметр', sort: 'diameter', numeric: true, value: (w) => text(w.diameter) },
  { key: 'tyreType', title: 'Тип шины', value: (w) => text(w.tyreType) },
  { key: 'tyreWidth', title: 'Ширина шины', sort: 'tyreWidth', numeric: true, value: (w) => text(w.tyreWidth) },
  { key: 'markingType', title: 'Тип маркировки',
    value: (w) => MARKING[w.markingType ?? ''] ?? text(w.markingType) },
  { key: 'treadType', title: 'Тип протектора',
    value: (w) => TREAD[w.treadType ?? ''] ?? text(w.treadType) },
  { key: 'construction', title: 'Тип конструкции', value: (w) => text(w.construction) },
  { key: 'tyreHeight', title: 'Высота шины', sort: 'tyreHeight', numeric: true, value: (w) => text(w.tyreHeight) },
  // Износ в процентах, как у клиента в Bazon (решение владельца 19.10.2026,
  // задача 0340): «30%» вписано — «30%» и показано.
  { key: 'wear', title: 'Износ', sort: 'wear', numeric: true, value: (w) => wearText(w.wearPercent) },
  { key: 'tyreBrand', title: 'Производитель шины', sort: 'tyreBrand',
    value: (w) => text(w.brand) },
  { key: 'tyreModel', title: 'Модель шины', value: (w) => text(w.model) },
  { key: 'season', title: 'Сезон', sort: 'season',
    value: (w) => SEASONS.find((s) => s.code === w.season)?.name ?? '' },
  { key: 'madeYear', title: 'Год производства', sort: 'madeYear', numeric: true, value: (w) => text(w.madeYear) },
  { key: 'discType', title: 'Тип диска', value: (w) => text(w.discType) },
  { key: 'discWidth', title: 'Ширина диска', numeric: true, value: (w) => text(w.discWidth) },
  { key: 'offset', title: 'Вылет', numeric: true, value: (w) => text(w.offsetMm) },
  { key: 'bolt', title: 'Сверловка', value: (w) => text(w.boltPattern) },
  { key: 'hub', title: 'Диаметр ЦО', numeric: true, value: (w) => text(w.hubBore) },
  { key: 'discBrand', title: 'Производитель диска', sort: 'discBrand',
    value: (w) => text(w.discBrand) },
  { key: 'discModel', title: 'Модель диска', value: (w) => text(w.discModel) },
  { key: 'oem', title: 'Номер производителя', value: (w) => text(w.oem) },
  { key: 'price', title: 'Цена', sort: 'price', numeric: true, value: (w) => money(w.price) },
  { key: 'description', title: 'Комментарий', value: (w) => text(w.description) },
  { key: 'note', title: 'Заметка', value: (w) => text(w.note) },
  { key: 'section', title: 'Секция', sort: 'section', value: (w) => text(w.section) },
  { key: 'created', filter: false, title: 'Создан', sort: 'created', value: (w) => day(w.createdAt) },
  { key: 'updated', filter: false, title: 'Изменён', value: (w) => day(w.updatedAt) },
  { key: 'updatedBy', title: 'Кто изменил', value: (w) => text(w.updatedByName) },
  { key: 'supply', title: 'Поставка', value: (w) => text(w.supply) },
  { key: 'partName', title: 'Наименование', value: (w) => text(w.partName) },
  { key: 'condition', title: 'Состояние',
    value: (w) => CONDITION[w.condition ?? ''] ?? text(w.condition) },
  // RunFlat дороже обычной вдвое, а LT от легковой того же размера
  // отличается нагрузкой: продать одно вместо другого — разные неприятности.
  { key: 'runFlat', title: 'RunFlat', value: (w) => flag(w.runFlat) },
  { key: 'lightTruck', title: 'Легкогрузовая (LT)', value: (w) => flag(w.lightTruck) },
  { key: 'speedIndex', title: 'Индекс скорости', value: (w) => text(w.speedIndex) },
  { key: 'loadIndex', title: 'Индекс нагрузки', numeric: true, value: (w) => text(w.loadIndex) },
  { key: 'donor', title: 'Номер донора', value: (w) => text(w.donorCode) },
  { key: 'published', title: 'Выгружать', value: (w) => (w.published ? 'Везде' : 'Нет') },
  { key: 'photoCount', filter: false, title: 'Количество фото', numeric: true,
    value: (w) => (w.photoCount === 0 ? '' : String(w.photoCount)) },
  { key: 'legacy', title: 'Старые данные', value: (w) => text(w.legacyCode) },
  { key: 'barcode', title: 'Ст. баркод', value: (w) => text(w.barcode) },
  { key: 'priceChanged', filter: false, title: 'Цена изменена в', value: (w) => day(w.priceChangedAt) },
  { key: 'priceChangedBy', title: 'Кто изменил цену', value: (w) => text(w.priceChangedByName) },
];

/**
 * Колонки, включённые по умолчанию.
 *
 * <p>Сорок пять колонок сразу — это таблица шириной в четыре экрана,
 * по которой ничего не найти. Показывается то, по чему колесо ищут глазами,
 * остальное владелец включает сам, и выбор запоминается.
 */
export const WHEEL_DEFAULT_VISIBLE = [
  'number', 'code', 'photo', 'set', 'setPieces', 'kind', 'diameter', 'tyreWidth', 'tyreHeight',
  'season', 'wear', 'tyreBrand', 'tyreModel', 'price', 'section',
];

const WHEEL_STORAGE_KEY = 'wheel-columns';

export function loadWheelVisible(): string[] {
  try {
    const saved = localStorage.getItem(WHEEL_STORAGE_KEY);
    if (saved === null) {
      return WHEEL_DEFAULT_VISIBLE;
    }
    const parsed: unknown = JSON.parse(saved);
    return Array.isArray(parsed) && parsed.every((k) => typeof k === 'string')
      ? (parsed as string[])
      : WHEEL_DEFAULT_VISIBLE;
  } catch {
    // Испорченная запись — не повод показать пустую таблицу.
    return WHEEL_DEFAULT_VISIBLE;
  }
}

export function saveWheelVisible(keys: string[]): void {
  try {
    localStorage.setItem(WHEEL_STORAGE_KEY, JSON.stringify(keys));
  } catch {
    // Приватный режим браузера запрещает запись: настройка не сохранится,
    // но таблица работать не перестанет.
  }
}

/**
 * Колесо в виде строки витрины — чтобы открывалось той же карточкой.
 *
 * <p>Карточка одна на оба вида товара: цена, списание и перемещение написаны
 * на складе, а не на виде товара, и второй экран разошёлся бы с первым
 * на первой же правке. Свойства колеса едут отдельным списком
 * ({@link wheelFields}) — в строке запчасти для них полей нет и быть
 * не должно.
 */
export function rowOfWheel(row: WheelRow): CatalogRow {
  const w = row.wheel;
  return {
    id: w.id, number: w.number, code: w.publicCode, title: w.title,
    qualityGrade: null, condition: w.condition,
    brand: null, model: null, generation: null, yearFrom: null, yearTo: null,
    body: null, engine: null, year: null, donorCode: w.donorCode,
    price: w.price, installationPrice: null, color: null,
    description: w.description, note: w.note,
    // Производителя карточка берёт из свойств колеса (wheelFields), где он
    // назван по-своему для шины и для диска. Здесь — пусто, иначе в карточке
    // появляются две строки «Производитель»: React ругается на повторённый
    // ключ, а человек видит поле дважды.
    manufacturer: null, marking: null, section: w.section,
    // Колонки «Ячейка» на вкладке колёс нет, а адрес карточка берёт своим
    // запросом по складам (`GET /api/parts/{id}/cells`) — читать тут нечего.
    cellCode: null,
    sideLr: null, sideFr: null, qty: Number(w.qty), oem: w.oem, crosses: null,
    photoUrl: row.photoUrl, supply: w.supply, equipment: null,
    partName: w.partName, published: w.published, barcode: w.barcode,
    legacyCode: w.legacyCode, dromAdNumber: null, videoUrl: null, textBlock: null,
    weightKg: null, dimensions: null, packageDimensions: null, packageWeightKg: null,
    createdAt: w.createdAt, updatedAt: w.updatedAt, updatedByName: w.updatedByName,
    priceChangedAt: w.priceChangedAt, priceChangedByName: w.priceChangedByName,
    photoCount: w.photoCount, stock: w.stock,
  };
}

/**
 * Свойства колеса для карточки — только заполненные, как и у запчасти.
 *
 * <p>Шинных полей и дисковых поровну, и показывать пустую половину значит
 * прятать заполненную среди прочерков.
 */
export function wheelFields(w: Wheel): Array<[string, string]> {
  const fields: Array<[string, string]> = [];
  const add = (title: string, value: string) => {
    if (value !== '') fields.push([title, value]);
  };

  add('Товар', kindName(w.kind));
  add('Номер комплекта', text(w.setNo));
  add('Штук в комплекте', w.setPieces == null ? '' : `${w.setPieces} шт`);
  add('Размер', sizeOf(w));
  if (w.kind !== 'DISC') {
    add('Тип шины', text(w.tyreType));
    add('Тип маркировки', MARKING[w.markingType ?? ''] ?? text(w.markingType));
    add('Тип протектора', TREAD[w.treadType ?? ''] ?? text(w.treadType));
    add('Сезон', SEASONS.find((s) => s.code === w.season)?.name ?? '');
    add('Износ', wearText(w.wearPercent));
    add('Год производства', text(w.madeYear));
    add('Индекс скорости', text(w.speedIndex));
    add('Индекс нагрузки', text(w.loadIndex));
    add('RunFlat', w.runFlat === true ? 'да' : '');
    add('Легкогрузовая (LT)', w.lightTruck === true ? 'да' : '');
  }
  if (w.kind !== 'TYRE') {
    add('Тип диска', text(w.discType));
    add('Сверловка', text(w.boltPattern));
    add('Вылет', w.offsetMm === null ? '' : `ET${w.offsetMm}`);
    add('Диаметр ЦО', text(w.hubBore));
  }
  // У сборки производители разные — шина Dunlop на диске Mitsubishi, —
  // и одной строкой «производитель» их не назвать.
  if (w.kind === 'ASSEMBLY') {
    add('Производитель шины', text(w.brand));
    add('Модель шины', text(w.model));
    add('Производитель диска', text(w.discBrand));
    add('Модель диска', text(w.discModel));
  } else {
    add('Производитель', text(w.brand ?? w.discBrand));
    add('Модель', text(w.model ?? w.discModel));
  }
  return fields;
}
